import { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Alert,
  Paper,
  Typography,
  InputAdornment,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  CircularProgress,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import axiosInstance from "../../axiosInstance";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import {
  parseColor,
  darkenColor,
  toRgbaString,
  isValidColor,
  getContrastTextColor,
  getGradientContrastTextColor,
} from "../../utils/colorUtils";
import {
  getSubdomainPrefix,
  isRootDomain,
  buildTenantLoginUrl,
  getRootDomain,
} from "../../utils/subdomain";

import fullLoginBg1080p from "../../assets/images/full-login-bg-1080p.jpg";
import cardLeafCorner from "../../assets/images/card-leaf-corner.png";
import durgaEmblem from "../../assets/images/durga-emblem.png";

// Two-leaf sprout icon matching the reference card design
const CardTopLeaf = ({ color = "#438e4a" }) => {
  const darkColor = darkenColor(color, 20);
  return (
    <Box
      component="svg"
      viewBox="0 0 32 32"
      sx={{ width: 38, height: 38, mb: 0.8, display: { xs: "none", md: "block" } }}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Left leaf */}
      <path
        d="M13.5 22.5C13.5 22.5 9 18.5 8.2 13C7.5 7.8 12.5 6 12.5 6C12.5 6 16.8 10.2 16.2 15.5C15.8 18.8 13.5 22.5 13.5 22.5Z"
        fill={color}
      />
      {/* Right leaf */}
      <path
        d="M15.5 23C15.5 23 18.5 16.5 25.5 12C31 8.5 32.5 10 32.5 10C32.5 10 31 17.5 24.5 21C19.5 23.8 15.5 23 15.5 23Z"
        fill={darkColor}
      />
    </Box>
  );
};

function LoginPage() {
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [prefix, setPrefix] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [hostInfo, setHostInfo] = useState({
    rootDomain: "",
    subdomain: null,
    isRoot: true,
  });

  const navigate = useNavigate();
  const { login } = useAuth();
  const { themeColor, employer, config, updateTheme } = useTheme();

  const isGradient = config?.themeType === "gradient" && config?.gradientColor1 && config?.gradientColor2;
  const gradientBg = isGradient
    ? `linear-gradient(135deg, ${config.gradientColor1} 0%, ${config.gradientColor2} 100%)`
    : null;
  const activeColor = (isGradient ? config.gradientColor1 : themeColor) || "#0A6836";
  const hoverColor = darkenColor(activeColor, 15);
  const shadowColor = toRgbaString(activeColor, 0.4);
  const shadowHover = toRgbaString(activeColor, 0.5);
  const autoContrast = isGradient
    ? getGradientContrastTextColor(config.gradientColor1, config.gradientColor2)
    : getContrastTextColor(activeColor);
  const contrastColor =
    config?.textColor && isValidColor(config.textColor)
      ? config.textColor
      : autoContrast;

  useEffect(() => {
    const user = localStorage.getItem("user");
    const token = localStorage.getItem("authToken");
    if (user && token) {
      try {
        const parsedUser = JSON.parse(user || "{}");
        navigate(parsedUser?.roleId === 3 ? "/list" : "/dashboard");
      } catch {
        navigate("/dashboard");
      }
      return;
    }

    const savedMobile = localStorage.getItem("rememberedMobile");
    if (savedMobile) {
      setMobile(savedMobile);
      setRememberMe(true);
    }
  }, [navigate]);

  useEffect(() => {
    const host = window.location.hostname;
    const subdomain = getSubdomainPrefix(host);
    const rootDomain = getRootDomain(host);
    setHostInfo({
      rootDomain,
      subdomain,
      isRoot: isRootDomain(host),
    });
    if (subdomain) {
      setPrefix(subdomain);
    }
  }, []);

  // If on root domain and user enters 10-digit mobile, fetch employer theme dynamically
  useEffect(() => {
    if (hostInfo.isRoot && mobile.length === 10 && !themeColor) {
      axiosInstance
        .post("/auth/get-slug", { mobile })
        .then((res) => {
          if (res.data?.success && res.data?.colorTheme) {
            updateTheme(res.data.colorTheme, res.data.config, {
              employerName: res.data.employerName,
              prefix: res.data.slug,
            });
          }
        })
        .catch(() => {});
    }
  }, [mobile, hostInfo.isRoot, themeColor, updateTheme]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // If on root domain and user entered a prefix, redirect to that subdomain
    if (hostInfo.isRoot && prefix.trim()) {
      const tenantUrl = buildTenantLoginUrl(prefix.trim(), window.location.hostname);
      if (tenantUrl) {
        window.location.href = tenantUrl;
        return;
      }
    }

    setLoading(true);

    try {
      const res = await axiosInstance.post("/auth/login", {
        mobile,
        password,
        tenantHost: window.location.hostname,
      });

      if (!res.data?.user || !res.data?.token) {
        setError("Invalid response from server");
        return;
      }

      if (rememberMe) {
        localStorage.setItem("rememberedMobile", mobile);
      } else {
        localStorage.removeItem("rememberedMobile");
      }

      // Save token
      localStorage.setItem("authToken", res.data.token);

      const userThemeColor = res.data.user?.colorTheme || res.data.user?.config?.themeColor;
      const userConfig = res.data.user?.config || {};

      const mappedUser = {
        name: res.data.user.fullName,
        userId: res.data.user.userId,
        roleId: res.data.user.roleId,
        passwordUpdated: res?.data?.user?.passwordUpdated,
        internalAdmin: res?.data?.user?.internalAdmin,
        employerId: res?.data?.user?.employerId ? res.data.user.employerId : "",
        employerName: res?.data?.user?.employerName ? res.data.user.employerName : "",
        prefix: res?.data?.user?.prefix ? res.data.user.prefix : "",
        colorTheme: userThemeColor,
        config: userConfig,
        _id: res?.data?.user?._id,
      };

      // Save to localStorage
      localStorage.setItem("user", JSON.stringify(mappedUser));

      login(mappedUser);
      if (userThemeColor) {
        updateTheme(userThemeColor, userConfig);
      }

      // Redirect
      navigate(mappedUser?.roleId === 3 ? "/list" : "/dashboard");
    } catch (err) {
      setError(err.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    mt: 1.8,
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      backgroundColor: "#ffffff",
      transition: "all 0.2s ease-in-out",
      "& fieldset": {
        borderColor: "#e2e8f0",
        borderWidth: "1.5px",
      },
      "&:hover fieldset": {
        borderColor: "#cbd5e1",
      },
      "&.Mui-focused fieldset": {
        borderColor: activeColor,
        borderWidth: "2px",
      },
    },
    "& .MuiOutlinedInput-input": {
      py: "13.5px",
    },
    "& .MuiInputLabel-root": {
      color: "#64748b",
      fontSize: "14.5px",
      "&.Mui-focused": {
        color: activeColor,
        fontWeight: 600,
      },
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        height: { xs: "auto", md: "100vh" },
        maxHeight: { xs: "none", md: "100vh" },
        width: "100vw",
        backgroundImage: { xs: "none", md: `url(${fullLoginBg1080p})` },
        backgroundColor: { xs: "#f8fafc", md: "transparent" },
        backgroundSize: "cover",
        backgroundPosition: "left center",
        backgroundRepeat: "no-repeat",
        overflowX: "hidden",
        overflowY: { xs: "auto", md: "hidden" },
        display: "flex",
        alignItems: "center",
        justifyContent: { xs: "center", md: "flex-end" },
        boxSizing: "border-box",
        pr: { xs: 2, sm: 3, md: 6, lg: 10, xl: 15 },
        pl: { xs: 2, sm: 3, md: 0 },
        py: { xs: 3, sm: 4, md: 1 },
      }}
    >
      {/* Floating Login Card on the right */}
      <Box
        sx={{
          width: "100%",
          maxWidth: { xs: "390px", sm: "420px", md: "440px", lg: "460px" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          my: "auto",
        }}
      >
        {/* White Floating Login Card */}
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: { xs: "20px", sm: "24px", md: "28px" },
            py: { xs: 3.5, sm: 4.2, md: 4.8 },
            px: { xs: 2.8, sm: 3.8, md: 4.5 },
            width: "100%",
            minHeight: { xs: "auto", sm: "490px", md: "520px" },
            backgroundColor: { xs: "#ffffff", md: "rgba(255, 255, 255, 0.98)" },
            boxShadow: {
              xs: "0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(226, 232, 240, 0.8)",
              md: "0 25px 60px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.85)",
            },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            maxHeight: { xs: "none", md: "95vh" },
            boxSizing: "border-box",
          }}
        >
          {/* Top Leaf Sprout Icon (Desktop only) */}
          <CardTopLeaf color={activeColor} />

          {/* Heading */}
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: "1.65rem", sm: "1.95rem" },
              fontWeight: 800,
              color: "#0e3c26",
              letterSpacing: "-0.02em",
              textAlign: "center",
              lineHeight: 1.2,
              mt: 0.5,
            }}
          >
            Welcome Back!
          </Typography>

          {/* Subtitle */}
          <Typography
            sx={{
              color: "#52796f",
              fontSize: "14px",
              textAlign: "center",
              mt: 0.6,
              mb: 2.2,
              px: 1,
              lineHeight: 1.45,
            }}
          >
            Login to your account to continue managing your store.
          </Typography>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{
                width: "100%",
                mb: 1.5,
                py: 0.4,
                px: 1.5,
                borderRadius: "12px",
                fontSize: "13.5px",
              }}
            >
              {error}
            </Alert>
          )}

          {/* Form */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ width: "100%", zIndex: 1 }}
          >
            <TextField
              label="Mobile Number"
              name="mobile"
              fullWidth
              required
              value={mobile}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                setMobile(val);
              }}
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneAndroidOutlinedIcon
                      sx={{ color: "#64748b", fontSize: 20, mr: 0.7 }}
                    />
                    <Box
                      component="span"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        pr: 0.9,
                        mr: 0.9,
                        borderRight: "1.5px solid #e2e8f0",
                        fontSize: "13.5px",
                        fontWeight: 600,
                        color: "#475569",
                      }}
                    >
                      🇮🇳 +91
                    </Box>
                  </InputAdornment>
                ),
                inputMode: "numeric",
              }}
              inputProps={{
                minLength: 10,
                maxLength: 10,
                pattern: "[0-9]{10}",
              }}
            />

            <TextField
              required
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon
                      sx={{ color: "#64748b", fontSize: 20, mr: 0.7 }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((p) => !p)}
                      edge="end"
                      size="small"
                      sx={{ color: "#64748b" }}
                    >
                      {showPassword ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Remember Me */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                mt: 1.2,
                mb: 0.4,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    size="small"
                    sx={{
                      py: 0.5,
                      color: "#94a3b8",
                      "&.Mui-checked": {
                        color: activeColor,
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: "13.5px",
                      color: "#334155",
                      fontWeight: 500,
                      userSelect: "none",
                    }}
                  >
                    Remember me
                  </Typography>
                }
              />
            </Box>

            {/* Login Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 2,
                mb: 1.8,
                height: "50px",
                borderRadius: "14px",
                background: gradientBg || activeColor,
                backgroundColor: !gradientBg ? activeColor : undefined,
                color: `${contrastColor} !important`,
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 700,
                letterSpacing: "0.02em",
                boxShadow: `0 6px 18px -2px ${shadowColor}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), filter 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  filter: "brightness(0.92) saturate(1.06)",
                  color: `${contrastColor} !important`,
                  boxShadow: `0 10px 24px -2px ${shadowHover}`,
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                  filter: "brightness(0.85)",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={22} sx={{ color: contrastColor }} />
              ) : (
                <>
                  <span style={{ color: contrastColor }}>Login</span>
                  <ArrowForwardRoundedIcon sx={{ fontSize: 20, color: contrastColor }} />
                </>
              )}
            </Button>

            {/* Leaf Divider */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "80%",
                mx: "auto",
                my: 2.2,
              }}
            >
              <Box sx={{ flex: 1, height: "1px", bgcolor: "#e2e8f0" }} />
              <Box
                sx={{
                  px: 1.5,
                  display: { xs: "none", md: "flex" },
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill={activeColor}>
                  <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3A4.49 4.49 0 0 0 8 20C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75C7 8 17 8 17 8z" />
                </svg>
              </Box>
              <Box sx={{ flex: 1, height: "1px", bgcolor: "#e2e8f0" }} />
            </Box>

            {/* Footer Trust Text */}
            <Typography
              sx={{
                textAlign: "center",
                fontSize: "12px",
                color: "#475569",
                fontWeight: 500,
                letterSpacing: "0.04em",
              }}
            >
              Trusted <span style={{ margin: "0 5px", color: "#94a3b8" }}>•</span> Fresh{" "}
              <span style={{ margin: "0 5px", color: "#94a3b8" }}>•</span> Reliable
            </Typography>
          </Box>

          {/* Corner Leaf Watermark (Desktop only) */}
          <Box
            component="img"
            src={cardLeafCorner}
            alt=""
            sx={{
              display: { xs: "none", md: "block" },
              position: "absolute",
              bottom: 0,
              right: 0,
              width: { xs: "75px", sm: "98px" },
              pointerEvents: "none",
              userSelect: "none",
              borderBottomRightRadius: "28px",
              opacity: 0.88,
              zIndex: 0,
            }}
          />
        </Paper>
      </Box>
    </Box>
  );
}

export default LoginPage;
